import * as Sentry from "@sentry/vue";

import RtLcaLogo from "@/assets/Logos/Logo.svg";
import { getUsersByTenantId } from "@/api/services/Frontegg";
import { useAuthStateStore } from "@/stores/Utils/auth/AuthState";
import type { ProjectActor } from "../types/projectVersion.types";

// created_by on trail/revision rows is a bare user id — names and avatars live in the Frontegg
// directory (the AgentUsageHistory pattern). One tenant-wide fetch, cached per tenant for the
// session; failures degrade to "Unknown user" rather than breaking the page.

/**
 * The system actor. Some rows are not written by a person: every project that existed before
 * versioning was given an "Initial revision" save point by the Hasura migration
 * `project_active_revision_required`, and every project created since gets one from the
 * `project_ensure_active_revision` trigger — both stamp created_by with this all-zero uuid.
 *
 * It is not a Frontegg user, so without this it would fall through to "Unknown user" on the
 * current revision of nearly every project. The two sides have to agree on the constant: the
 * database writes it, this file is the only place that renders it.
 */
export const SYSTEM_ACTOR_ID = "00000000-0000-0000-0000-000000000000";

export const SYSTEM_ACTOR: ProjectActor = Object.freeze({
  id: SYSTEM_ACTOR_ID,
  name: "Real-Time LCA Support",
  avatarUrl: RtLcaLogo,
});

let cachedTenantId: string | null = null;
let cachedDirectory: Record<string, ProjectActor> = {};
let inflight: Promise<Record<string, ProjectActor>> | null = null;

export const resolveProjectActorDirectory = async (): Promise<Record<string, ProjectActor>> => {
  const tenantId = useAuthStateStore().user?.tenantId;
  if (!tenantId) return {};
  if (cachedTenantId === tenantId) return cachedDirectory;
  if (inflight) return inflight;

  inflight = (async () => {
    try {
      // The Frontegg service returns untyped axios data; narrow to the fields we read.
      const response = (await getUsersByTenantId(tenantId)) as {
        items?: Array<{ id: string; name?: string | null; email?: string | null; profilePictureUrl?: string | null }>;
      };
      const directory: Record<string, ProjectActor> = {};
      for (const user of response?.items ?? []) {
        directory[user.id] = {
          id: user.id,
          name: user.name || user.email || "Unknown user",
          email: user.email ?? undefined,
          avatarUrl: user.profilePictureUrl ?? undefined,
        };
      }
      cachedTenantId = tenantId;
      cachedDirectory = directory;
      return directory;
    } catch (e) {
      // Non-fatal: rows render with "Unknown user" and the page stays useful.
      Sentry.captureException(e);
      return {};
    } finally {
      inflight = null;
    }
  })();

  return inflight;
};

export const actorFrom = (directory: Record<string, ProjectActor>, userId: string | null | undefined): ProjectActor => {
  if (!userId) return { id: "unknown", name: "Unknown user" };
  // Ahead of the lookup: the directory holds Frontegg users and can never contain the sentinel,
  // so it would otherwise land on the "Unknown user" fallback below.
  if (userId === SYSTEM_ACTOR_ID) return SYSTEM_ACTOR;
  return directory[userId] ?? { id: userId, name: "Unknown user" };
};
